import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /**
   * Contenu du dialog chargé à la demande (mode `defer_template`). Une seule
   * promesse par composant : le survol lance le chargement, le clic la réutilise
   * au lieu d'en déclencher un second.
   * @type {Promise<DocumentFragment | null> | null}
   */
  #deferredContent = null;
  /** @type {AbortController | null} */
  #deferredAbortController = null;
  /** @type {IntersectionObserver | null} */
  #deferredObserver = null;

  get productPageUrl() {
    // Match both the native <product-card> and the Axome <ax-product-card>.
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (
      this.closest('product-card, ax-product-card')
    );
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    // Match both the native <product-card> and the Axome <ax-product-card>.
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (
      this.closest('product-card, ax-product-card')
    );
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));

    // Axome — mode différé : le contenu du dialog n'est pas dans la page, on le
    // charge au premier survol pour que le clic reste perçu comme instantané.
    // `pointerenter` couvre le bureau. Au toucher il ne se déclenche pas, mais
    // `pointerdown` part dès l'appui — soit ~100 ms d'avance sur le `click`.
    if (this.dataset.axQuickAddDeferred) {
      for (const type of ['pointerenter', 'pointerdown', 'focusin']) {
        this.addEventListener(type, this.#prefetchDeferredContent, { once: true, passive: true });
      }
      this.#observeForPrefetch();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    this.#deferredAbortController?.abort();
    this.#deferredObserver?.disconnect();
    this.#deferredObserver = null;
    for (const type of ['pointerenter', 'pointerdown', 'focusin']) {
      this.removeEventListener(type, this.#prefetchDeferredContent);
    }
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
    // Le contenu différé porte les états de stock et les prix : il périme aussi.
    this.#deferredContent = null;
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    // Axome — Nutrimuscle quick-add : utilise le template inline embarqué dans
    // <quick-add-component> (rendu par snippets/quick-add.liquid) au lieu de
    // fetcher la page produit. Plus rapide (pas de réseau) + layout dédié.
    const axTemplate = /** @type {HTMLTemplateElement | null} */ (
      this.querySelector('template[data-ax-quick-add-template]')
    );
    if (axTemplate) {
      const modalContent = document.getElementById('quick-add-modal-content');
      if (modalContent) {
        const fragment = axTemplate.content.cloneNode(true);
        modalContent.replaceChildren(fragment);
      }
      this.#openQuickAddModal();
      return;
    }

    // Axome — mode différé : même contenu, chargé à la demande. Le survol a
    // normalement déjà lancé la requête ; on réutilise sa promesse.
    if (this.dataset.axQuickAddDeferred) {
      const fragment = await this.#prefetchDeferredContent();
      const modalContent = document.getElementById('quick-add-modal-content');
      if (fragment && modalContent) {
        modalContent.replaceChildren(fragment.cloneNode(true));
        this.#openQuickAddModal();
        return;
      }
      // Le chargement a échoué (réseau, 404) : on retombe sur la page produit
      // plutôt que d'ouvrir un dialog vide.
    }

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Axome — réchauffe le contenu du dialog dès que la carte approche de l'écran,
   * pendant un temps mort du navigateur.
   *
   * Sans cela, un premier clic « à froid » attendait la requête : 250 ms mesurés
   * sur une connexion de bureau, davantage en mobile. Le but du chargement différé
   * est d'alléger le **chemin critique** — le premier rendu et la grille Nosto —
   * pas de déplacer l'attente sur l'acheteur. En préchargeant à l'approche, la
   * grille reste allégée de 490 ko et le clic retrouve son coût d'origine.
   *
   * `requestIdleCallback` cède la main tant que le navigateur a du travail : le
   * réchauffage ne concurrence jamais l'affichage de la grille.
   */
  #observeForPrefetch() {
    if (!('IntersectionObserver' in window)) {
      this.#prefetchDeferredContent();
      return;
    }
    const differer = window.requestIdleCallback ?? ((cb) => setTimeout(cb, 200));
    this.#deferredObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#deferredObserver?.disconnect();
        this.#deferredObserver = null;
        differer(() => this.#prefetchDeferredContent(), { timeout: 3000 });
      },
      // Un écran et demi d'avance : sur l'accueil, la première rangée de cartes
      // est à ~1520 px pour un écran de ~870 px. Avec 600 px de marge elle
      // restait froide, et un clic juste après le premier défilement coûtait
      // 249 ms au lieu de 35.
      { rootMargin: '1200px 0px' }
    );
    this.#deferredObserver.observe(this);
  }

  /**
   * Axome — charge le contenu du dialog via la Section Rendering API, en mode
   * `defer_template`. Rend exactement le même HTML que le `<template>` inline,
   * au wrapper `#shopify-section-*` près.
   *
   * Appelé au survol (préchargement) puis au clic : la promesse est mémorisée,
   * donc deux appels ne font qu'une requête. Elle est remise à zéro sur échec
   * pour qu'un survol suivant puisse réessayer, et à chaque mise à jour du
   * panier (les états de stock changent).
   *
   * @returns {Promise<DocumentFragment | null>}
   */
  #prefetchDeferredContent = () => {
    if (this.#deferredContent) return this.#deferredContent;

    const productUrl = this.dataset.axQuickAddDeferred;
    if (!productUrl) return Promise.resolve(null);

    this.#deferredAbortController?.abort();
    this.#deferredAbortController = new AbortController();

    const url = new URL(productUrl, location.origin);
    url.searchParams.set('section_id', 'section-rendering-ax-quick-add-content');

    this.#deferredContent = fetch(url.toString(), { signal: this.#deferredAbortController.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Quick-add content: HTTP ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const root = doc.querySelector('.ax-quick-add');
        if (!root) throw new Error('Quick-add content: .ax-quick-add absent de la réponse');
        const fragment = document.createDocumentFragment();
        fragment.appendChild(root);
        return fragment;
      })
      .catch((error) => {
        // Un abort est volontaire (composant démonté) : rien à signaler.
        if (error?.name !== 'AbortError') {
          this.#deferredContent = null;
          console.error(error);
        }
        return null;
      });

    return this.#deferredContent;
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    // Axome — matcher aussi `ax-product-card`. Sans ça, quand l'event vient du dialog
    // (variantSelected dans la quick-add modal), `closest('product-card')` retourne null
    // des deux côtés et `null !== null` est false : le filtre laisse passer et on
    // mute par erreur le `data-quick-add-button` de la card, ce qui fait apparaître
    // un faux add-to-cart-component dessus.
    const eventCard = event.target.closest('product-card, ax-product-card');
    const myCard = this.closest('product-card, ax-product-card');
    if (eventCard !== myCard) return;
    if (eventCard === null) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  /**
   * Axome — fetch en cours du swap inter-coloris (quick-add apparel).
   * @type {AbortController | null}
   */
  #colorSwapAbortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    // Axome — image principale dynamique (Nutrimuscle quick-add)
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateHeroImage);

    // Axome — swap inter-coloris : intercepte le clic sur un swatch couleur DANS
    // la modale (apparel) pour fetch + swap le produit-coloris au lieu de naviguer
    // vers sa PDP. Délégué sur le dialog (persiste à travers les swaps).
    this.addEventListener('click', this.#handleColorSwatchClick, { signal: this.#abortController.signal });

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#colorSwapAbortController?.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Axome — intercepte le clic sur un lien swatch couleur du quick-add apparel et
   * swap le produit-coloris cible dans la modale (reste dans le quick-add) au lieu
   * de naviguer vers la PDP du coloris.
   * @param {MouseEvent} event
   */
  #handleColorSwatchClick = async (event) => {
    // Respecte les modificateurs (nouvel onglet, etc.) et les clics non-primaires.
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const target = /** @type {HTMLElement} */ (event.target);
    const link = target.closest('.ax-quick-add .ax-color-switch a[href]');
    if (!(link instanceof HTMLAnchorElement)) return;

    event.preventDefault();

    const root = link.closest('.ax-quick-add');
    if (!root || root.hasAttribute('data-ax-color-swapping')) return;

    await this.#swapColorProduct(link.href, root);
  };

  /**
   * Axome — fetch le rendu quick-add complet du produit-coloris cible et remplace
   * le contenu de la modale. Recrée les custom elements (variant-options-slider,
   * variant-picker) → ils se ré-initialisent via connectedCallback.
   * @param {string} productUrl - URL du produit-coloris (href du swatch)
   * @param {Element} currentRoot - Le `.ax-quick-add` actuellement affiché
   */
  async #swapColorProduct(productUrl, currentRoot) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    this.#colorSwapAbortController?.abort();
    this.#colorSwapAbortController = new AbortController();

    currentRoot.setAttribute('data-ax-color-swapping', '');

    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('section_id', 'section-rendering-ax-quick-add-content');

    try {
      const response = await fetch(url.toString(), { signal: this.#colorSwapAbortController.signal });
      if (!response.ok) throw new Error(`Failed to fetch quick-add content: HTTP ${response.status}`);

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const newRoot = html.querySelector('.ax-quick-add');
      if (!newRoot) throw new Error('No .ax-quick-add found in fetched quick-add content');

      // replaceChildren (pas morph) : on change de produit → on veut des custom
      // elements neufs dont connectedCallback re-init le Swiper couleur et le
      // variant-picker (Taille). Morph préserverait les éléments et n'appellerait
      // pas connectedCallback.
      modalContent.replaceChildren(document.importNode(newRoot, true));

      // Réactive l'observer lazy pour les images du nouveau contenu (si présentes).
      document.dispatchEvent(new CustomEvent('reactivateLazyObserver'));
    } catch (error) {
      if (error.name === 'AbortError') return;
      currentRoot.removeAttribute('data-ax-color-swapping');
      // Fallback : on laisse la navigation native se faire en dernier recours.
      window.location.assign(productUrl);
    }
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  /**
   * Axome — met à jour l'image principale (.ax-quick-add__image) au changement de variante.
   * Lit la nouvelle image dans le HTML fetché (section-rendering-ax-quick-add) porté par
   * l'event variantUpdate, et swap src/srcset/sizes/alt sans recharger la modal.
   * @param {CustomEvent} event
   */
  #updateHeroImage = (event) => {
    const html = event.detail?.data?.html;
    if (!html) return;
    const newImage = html.querySelector('[data-ax-quick-add-media] .ax-quick-add__image');
    const currentImage = this.querySelector('[data-ax-quick-add-media] .ax-quick-add__image');
    if (!newImage || !currentImage) return;
    for (const attr of ['src', 'srcset', 'sizes', 'alt']) {
      const value = newImage.getAttribute(attr);
      if (value != null) currentImage.setAttribute(attr, value);
    }
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
